import React, { useState, useEffect } from 'react';
import { ActiveCustomer } from '../../types/organizations';

interface CustomerInvoicesModalProps {
  customer: ActiveCustomer;
  organizationId: string;
  isOpen: boolean;
  onClose: () => void;
  invoices: any[];
}

const CustomerInvoicesModal: React.FC<CustomerInvoicesModalProps> = ({ customer, organizationId, isOpen, onClose, invoices }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Set loading state based on whether invoices are available
  useEffect(() => {
    if (isOpen) {
      if (invoices.length === 0) {
        setLoading(true);
        setError(null);
      } else {
        setLoading(false);
        setError(null);
      }
    }
  }, [isOpen, invoices]);

  if (!isOpen) return null;

  // Helper function to calculate days overdue
  const calculateDaysOverdue = (dueDate: string, status: string, fullyPaidDate?: string): number | null => {
    if (!dueDate) return null;
    
    const due = new Date(dueDate);
    let compareDate: Date;
    
    if (status.toLowerCase() === 'paid' && fullyPaidDate) {
      // For paid invoices, compare fully_paid_on_date with due_date
      compareDate = new Date(fullyPaidDate);
    } else {
      // For unpaid invoices, compare today with due_date
      compareDate = new Date();
    }
    
    const diffTime = compareDate.getTime() - due.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    return diffDays;
  };

  // Helper function to get status styling
  const getStatusStyling = (status: string) => {
    switch (status.toLowerCase()) {
      case 'paid':
        return 'bg-green-100 text-green-800';
      case 'overdue':
        return 'bg-red-100 text-red-800';
      case 'current':
        return 'bg-blue-100 text-blue-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  // Helper function to get overdue styling
  const getOverdueStyling = (daysOverdue: number | null, status: string) => {
    if (daysOverdue === null) return 'text-gray-500';
    if (status.toLowerCase() === 'paid') return 'text-gray-600';
    if (daysOverdue > 0) return 'text-red-600 font-semibold';
    if (daysOverdue === 0) return 'text-yellow-600 font-semibold';
    return 'text-green-600';
  };

  // Helper function to get category styling (synced with CustomersTab)
  const getCategoryStyling = (category: string): string => {
    const categoryLower = category.toLowerCase();

    if (categoryLower.includes('regular') || categoryLower.includes('standard')) {
      return 'bg-blue-100 text-blue-800';
    } else if (categoryLower.includes('premium') || categoryLower.includes('vip')) {
      return 'bg-purple-100 text-purple-800';
    } else if (categoryLower.includes('at-risk') || categoryLower.includes('risk')) {
      return 'bg-red-100 text-red-800';
    } else if (categoryLower.includes('new') || categoryLower.includes('prospect')) {
      return 'bg-green-100 text-green-800';
    } else if (categoryLower.includes('inactive') || categoryLower.includes('dormant')) {
      return 'bg-gray-100 text-gray-800';
    } else {
      return 'bg-indigo-100 text-indigo-800';
    }
  };


  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-6xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex justify-between items-start mb-6">
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-2">
                <h3 className="text-2xl font-bold text-gray-900">{customer.name}</h3>
                {customer.selected_category && (
                  <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${getCategoryStyling(customer.selected_category)}`}>
                    {customer.selected_category}
                  </span>
                )}
              </div>
              {customer.customer_profile_summary && (
                <p className="text-sm text-gray-600 line-clamp-3 mb-2">
                  {customer.customer_profile_summary}
                </p>
              )}
              <h4 className="text-lg font-semibold text-gray-800">Invoices</h4>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 text-3xl ml-4"
            >
              ×
            </button>
          </div>

          {/* Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <div className="bg-blue-50 p-4 rounded-lg">
              <div className="text-2xl font-bold text-blue-900">
                {customer.invoice_metadata?.outstanding_invoice_count || 0}/{customer.invoice_metadata?.last_12m_invoice_count || 0}
              </div>
              <div className="text-blue-600 text-sm">LTM Invoices</div>
            </div>
            <div className="bg-green-50 p-4 rounded-lg">
              <div className="text-2xl font-bold text-green-900">
                ${(customer.invoice_metadata?.total_receivable || 0).toLocaleString()}
              </div>
              <div className="text-green-600 text-sm">Total Receivable</div>
            </div>
            <div className="bg-purple-50 p-4 rounded-lg">
              <div className="text-2xl font-bold text-purple-900">
                ${(customer.invoice_metadata?.last_12m_value || 0).toLocaleString()}
              </div>
              <div className="text-purple-600 text-sm">LTM Value</div>
            </div>
          </div>

          {/* Invoices Table */}
          <div className="overflow-x-auto">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mr-3"></div>
                <span className="text-gray-600">Loading invoices...</span>
              </div>
            ) : error ? (
              <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded text-center">
                {error}
              </div>
            ) : (
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Invoice #
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Date
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Due Date
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Amount
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Fully Paid At
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Days Overdue
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {invoices.length > 0 ? (
                    invoices.map((invoice: any, index: number) => {
                      const daysOverdue = calculateDaysOverdue(
                        invoice.due_date_at || invoice.due_date, 
                        invoice.status, 
                        invoice.fully_paid_on_date
                      );
                      
                      return (
                        <tr key={invoice.invoice_id || invoice.id || index} className="hover:bg-gray-50">
                          <td className="px-6 py-4 text-sm font-medium text-gray-900">
                            <div className="space-y-1">
                              <div className="font-medium">
                                {invoice.invoice_number || 'N/A'}
                              </div>
                              {/* Latest Note Preview */}
                              {invoice.latest_note && (
                                <div className="mt-1 flex items-center w-full">
                                  <div className="w-1 h-4 bg-gray-400 mr-3 flex-shrink-0"></div>
                                <span className="text-sm text-gray-600 italic line-clamp-2 flex-1 min-w-0">
                                  {invoice.latest_note.content}
                                </span>
                                </div>
                              )}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {invoice.date_at || invoice.date ? new Date(invoice.date_at || invoice.date).toLocaleDateString() : 'N/A'}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {invoice.due_date_at || invoice.due_date ? new Date(invoice.due_date_at || invoice.due_date).toLocaleDateString() : 'N/A'}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            ${(invoice.status?.toLowerCase() === 'paid' 
                              ? (invoice.amount || invoice.total || 0) 
                              : (invoice.amount_due || invoice.outstanding_amount || 0)
                            ).toLocaleString()}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusStyling(invoice.status)}`}>
                              {invoice.status || 'Unknown'}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {invoice.fully_paid_on_date ? new Date(invoice.fully_paid_on_date).toLocaleDateString() : 'N/A'}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm">
                            <span className={getOverdueStyling(daysOverdue, invoice.status)}>
                              {daysOverdue !== null ? (
                                daysOverdue > 0 ? `+${daysOverdue} days overdue` :
                                daysOverdue === 0 ? (invoice.status?.toLowerCase() === 'paid' ? 'Paid on time' : '0') :
                                invoice.status?.toLowerCase() === 'paid' ? `${Math.abs(daysOverdue)} days early` : '0'
                              ) : 'N/A'}
                            </span>
                          </td>
                        </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td colSpan={7} className="px-6 py-4 text-center text-sm text-gray-500">
                        No invoices found for this customer.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            )}
          </div>

          {/* Footer */}
          <div className="mt-6 flex justify-end">
            <button
              onClick={onClose}
              className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400 transition-colors"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CustomerInvoicesModal;
